import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Button, Card, FormGroup, Input, Textarea } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { FormattedDate, FormattedMinutes } from "utils/DateUtils";
import { Box, CircularProgress, Grid2 as Grid, IconButton, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { DemurrageDocumentsVoyageQuery, SuggestLaytime_DemurrageDetailsQuery } from "__generated__/graphql";
import {
	LaytimeCountingEvent,
	AgreementTypeEnum,
	CallType,
} from "__generated__/graphql";
import { LocationLink, ShipLink } from "utils/LinkUtils";
import { FormArrayContainer } from "components/cck/FormArray";
import { Controller, useForm, useWatch } from "react-hook-form";
import { CallWithCargoAction, Cargo } from "view/cargos/types/cargo";
import LaytimeCountingSelect from "view/charters/components/selects/LaytimeCountingSelect";
import DatePicker from "components/cck/DatePicker";
import {
	charterHasLaytimeActionWith,
	getLaytimePropertiesInput,
	laytimeMinutesToHHMM,
	laytimeTextToMinutes,
} from "utils/cargoActionUtils";
import { DataFormat, Language } from "utils";
import { useStoreState } from "ezStore";
import { SalesCharter } from "view/charters/types/charter";
import {
	AccessTimeFilled,
	AutoFixHigh,
	BrowseGallery,
	InsertDriveFile,
	ManageHistory,
	Calculate,
	Payments,
	Restore,
} from "@mui/icons-material";
import { DetailsBlock, Height } from "view/charters/dossier/Blocks";
import {
	demurrageCharter_query,
	demurrageDocumentsVoyage_query,
	suggestLaytime_query,
	updateLaytimeAndDeclare_mutation,
} from "view/charters/utils/DemurrageUtils";
import VoyageDocuments from "view/voyages/components/documents/VoyageDocuments";
import FormField from "components/cck/FormField";
import { Agreement } from "types/index";
import { LaytimeCountingOption } from "view/charters/types/demurrage";
import DemurrageTimesheet from "view/charters/sections/DemurrageTimesheet";
import { StatusChangeFragmentFragment as StatusChangeFragment } from "__generated__/view/statuses/graphql/fragments.generated";
import StatusChanger from "view/statuses/components/changer/StatusChanger";
import { StatusType } from "__generated__/enums";
import { GetAllCallsWithLaytimeCalculationDocument, GetAllCallsWithLaytimeCalculationQuery } from "__generated__/view/calls/graphql/call.queries.generated";
import { UpdateLaytimeCalculationDocument, UpdateLaytimeCalculationMutation } from "__generated__/view/calls/graphql/call.mutations.generated";
import { CopyLaytimeCalculationsDocument } from "__generated__/view/charters/graphql/charter.mutations.generated";
import EditIcon from "@mui/icons-material/Edit";
import { DemurrageCalculationRoundingScope } from "__generated__/enums.ts";
import DocumentDrawer from "components/documents/DocumentDrawer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShip } from "@fortawesome/pro-solid-svg-icons";
import { LaytimeCalculation as LaytimeCalculationType } from "view/charters/types/demurrage";
import { useConfirm } from "components/hooks/useConfirm";
import { ContentCopy } from "@mui/icons-material";
import { DemurrageCharterQuery } from "__generated__/graphql.ts";
import { ChildProps } from "types/commons";

type LaytimeTabProps = {
	charter: SalesCharter;
	voyageId: number;
	refetch: () => void;
}

enum CharterSide {
	Sales = "SALES",
	Purchase = "PURCHASE"
}

type CallLaytimeCardProps = {
	call: CallWithCargoAction;
	laytimeCalculation?: LaytimeCalculationType;
	charterId: number;
	editingCallId: number | null;
	setEditingCallId: (id: number | null) => void;
	refetch: () => void;
}

type CallLaytimeForm = {
	startLaytimeCountingOption?: LaytimeCountingOption;
	endLaytimeCountingOption?: LaytimeCountingOption;
	startLaytimeCountingTimestamp?: number;
	endLaytimeCountingTimestamp?: number;
	startLaytimeCountingLabel?: string;
	endLaytimeCountingLabel?: string;
}

type DemurrageEditFormType = {
	cleaningLaytimeText?: string;
	cleaningLaytime?: number;
	loadingLaytimeText?: string;
	loadingLaytime?: number;
	dischargingLaytimeText?: string;
	dischargingLaytime?: number;
	nitrogenPurgingLaytimeText?: string;
	nitrogenPurgingLaytime?: number;
	totalLaytimeText?: string;
	totalLaytime?: number;
	remarks?: string;
}

type CallLaytimeCalculationFormProps = {
	call: CallWithCargoAction;
	laytimeCalculation?: LaytimeCalculationType;
	charterId: number;
	onCancel: () => void;
	onClose: (id: number) => void;
	refetch: () => void;
}

const DemurrageTab = ({ charter, voyageId, refetch }: LaytimeTabProps) => {
	const [ side, setSide ] = useState<CharterSide>(CharterSide.Sales);

	const purchaseCharter = charter.purchaseCharter;
	const activeCharterId = side === CharterSide.Purchase && purchaseCharter ? purchaseCharter.id : charter.id;

	const { data: { voyage } = {}, refetch: refetchVoyage } = useQuery(demurrageDocumentsVoyage_query, {
		variables: {
			voyageId: voyageId,
		},
	});

	const { data: callsData, refetch: refetchCalls } = useQuery<GetAllCallsWithLaytimeCalculationQuery>(GetAllCallsWithLaytimeCalculationDocument,
		{
			variables: {
				search: {
					voyage: voyageId,
				},
			},
		},
	);

	const { data: { charter: activeCharter } = {}, loading, refetch: refetchCharter } = useQuery(demurrageCharter_query, {
		variables: { id: activeCharterId },
	});

	// Agreement from the active charter, so purchase suggestions/prefill work.
	const agreement = activeCharter?.contract?.agreements?.find(
		(a) => a?.type === AgreementTypeEnum.DemurrageCalculation,
	) as Agreement | undefined;

	const { data: suggestionData, refetch: refetchSuggestion } = useQuery(suggestLaytime_query, {
		variables: { charterId: activeCharterId, agreementId: agreement?.id ?? 0 },
		skip: !agreement?.id,
	});

	const laytimeCalculations = activeCharter?.laytimeCalculations
		.filter((l): l is LaytimeCalculationType => (!l.startStatus || !!l.startStatus?.cargo) && (!l.endStatus || !!l.endStatus?.cargo));

	const refetchAll = () => {
		refetchCharter();
		refetch();
	};

	if (voyage == null) {
		return <></>;
	}

	if (loading || !activeCharter) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center">
				<CircularProgress size={24} />
			</Box>
		);
	}

	return (
		<>
			<Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 2 }}>
				<Tabs value={side} onChange={(_event, value: CharterSide) => setSide(value)}>
					<Tab value={CharterSide.Sales} label={intl.formatMessage({ id: "SALES" })} />
					<Tab value={CharterSide.Purchase} disabled={purchaseCharter == null} label={intl.formatMessage({ id: "PURCHASE" })} />
				</Tabs>
			</Box>

			<Grid container spacing={2}>
				<Grid size={{ lg: 6, md: 6, xs: 12 }}>
					<DemurrageBlock
						charter={activeCharter}
						salesCharter={charter}
						voyage={voyage}
						agreement={agreement}
						refetch={refetchAll}
						laytimeSuggestion={suggestionData?.suggestLaytime}
					/>
					<DemurrageTimesheet
						charter={charter}
						voyage={voyage}
						refetch={refetchAll}
						refetchCalls={refetchCalls}
						refetchSuggestion={refetchSuggestion}
					/>
				</Grid>
				<Grid size={{ lg: 6, md: 6, xs: 12 }}>
					<LaytimeCalculation
						callsData={callsData}
						laytimeCalculations={laytimeCalculations ?? []}
						charterId={activeCharterId}
						side={side}
						salesCharterId={charter.id}
						purchaseCharterId={purchaseCharter?.id}
						refetch={refetchAll}
						refetchCalls={refetchCalls}
						refetchSuggestion={refetchSuggestion}
					/>
					<DemurrageDocuments
						refetch={refetchVoyage}
						voyage={voyage}
					/>
				</Grid>
			</Grid>
		</>
	);
};

type LaytimeCalculationProps = {
	callsData: GetAllCallsWithLaytimeCalculationQuery | undefined;
	laytimeCalculations: LaytimeCalculationType[];
	charterId: number;
	side: CharterSide;
	salesCharterId: number;
	purchaseCharterId?: number;
	refetch: () => void;
	refetchCalls: () => Promise<unknown>;
	refetchSuggestion: () => Promise<unknown>;
};

const LaytimeCalculation = ({
	callsData,
	laytimeCalculations,
	charterId,
	side,
	salesCharterId,
	purchaseCharterId,
	refetch,
	refetchCalls,
	refetchSuggestion,
}: LaytimeCalculationProps) => {
	const [ editingCallId, setEditingCallId ] = useState<number | null>(null);
	const confirm = useConfirm();

	const [ copyLaytimeCalculations, { loading: copying } ] = useMutation(CopyLaytimeCalculationsDocument);

	const filteredCalls = callsData?.allCalls?.filter(call =>
		call?.cargos.some(action => action.includedForLaytimeCalculation),
	) ?? [];

	const laytimeByCallId = useMemo(() => {
		const map = new Map<number, LaytimeCalculationType>();
		laytimeCalculations.forEach(l => map.set(Number(l.callId), l));
		return map;
	}, [ laytimeCalculations ]);

	const totalLaytime = filteredCalls.reduce((sum, call) => {
		const actualLaytime = laytimeByCallId.get(Number(call?.id ?? -1))?.actualLaytime ?? 0;
		return sum + actualLaytime;
	}, 0);

	const onCopyFromSales = () => {
		if (purchaseCharterId == null) {
			return;
		}
		confirm({
			type: "WARNING",
			title: intl.formatMessage({ id: "COPY_LAYTIME_FROM_SALES" }),
			content: <div>{intl.formatMessage({ id: "COPY_LAYTIME_FROM_SALES_CONFIRM" })}</div>,
		}).then(() => {
			copyLaytimeCalculations({
				variables: {
					fromCharterId: salesCharterId,
					toCharterId: purchaseCharterId,
				},
			}).then(() => {
				refetchCalls().then(refetchSuggestion).then(refetch);
			});
		}).catch(() => {
			// confirm dialog cancelled — no-op
		});
	};

	return <div style={{ marginBottom: 16 }}>
		<Card
			title={intl.formatMessage({ id: "LAYTIME" })}
			subtitle={totalLaytime ? <FormattedMinutes minutes={totalLaytime} /> : undefined}
			icon={<AccessTimeFilled fontSize="small" color="primary" />}
			disablePadding
			actions={side === CharterSide.Purchase ? [
				{
					icon: <ContentCopy fontSize="small" />,
					tooltip: intl.formatMessage({ id: "COPY_LAYTIME_FROM_SALES" }),
					onClick: onCopyFromSales,
					disabled: copying || purchaseCharterId == null,
				},
			] : undefined}
		>
			<FormArrayContainer>
				<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
					{filteredCalls.length > 0 && filteredCalls
						.sort((a, b) => (a?.pta ?? 0) - (b?.pta ?? 0))
						.map((call) => (
							<DemurrageCall
								key={call?.id}
								// @ts-ignore
								call={call as CallWithCargoAction}
								laytimeCalculation={laytimeByCallId.get(Number(call?.id ?? -1))}
								charterId={charterId}
								editingCallId={editingCallId}
								setEditingCallId={setEditingCallId}
								refetch={() => {
									refetchCalls().then(refetchSuggestion).then(refetch);
								}}
							/>
						))}
				</div>
			</FormArrayContainer>
		</Card>
	</div>;
};

const DemurrageCall = ({ call, laytimeCalculation, charterId, editingCallId, setEditingCallId, refetch }: CallLaytimeCardProps) => {
	const { location, cargos } = call;
	const isEditing = editingCallId === call.id;

	const actualLaytime = laytimeCalculation?.actualLaytime;
	const connectingShip = cargos.find(cargoAction => cargoAction?.connectingShipPortVisit?.ship?.name)?.connectingShipPortVisit?.ship;

	return (
		<Card
			title={
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
					<LocationLink location={location} />

					{connectingShip && (
						<>
							<FontAwesomeIcon icon={faShip} style={{ height: 18, width: 18, color: "gray" }} />
							<ShipLink ship={connectingShip} />
						</>
					)}
				</Box>
			}
			subtitle={actualLaytime ? <FormattedMinutes minutes={actualLaytime} /> : undefined}
			actions={[
				{
					icon: <EditIcon fontSize="small" />,
					onClick: () => setEditingCallId(call.id),
					disabled: editingCallId !== null,
				},
			]}
		>
			{isEditing ? (
				<CallLaytimeCalculationForm
					call={call}
					laytimeCalculation={laytimeCalculation}
					charterId={charterId}
					onClose={() => {
						setEditingCallId(null);
					}}
					onCancel={() => {
						setEditingCallId(null);
					}}
					refetch={refetch}
				/>
			) : laytimeCalculation ? (
				<CallLaytimeDetails laytimeCalculation={laytimeCalculation} />
			) : null}
		</Card>
	);
};

const convertToLaytimeOption = (
	event?: LaytimeCountingEvent,
	status?: StatusChangeFragment & { cargo: Pick<Cargo, "id" | "cargoNumber"> },
): LaytimeCountingOption | undefined => {
	if (status) {
		return { type: "status", statusChange: status };
	} else if (event) {
		return { type: "event", event };
	}
	return undefined;
};

const CallLaytimeCalculationForm = ({ call, laytimeCalculation, charterId, onCancel, onClose, refetch }: CallLaytimeCalculationFormProps) => {

	const defaultValues = {
		startLaytimeCountingOption: convertToLaytimeOption(
			laytimeCalculation?.startEvent ?? undefined,
			laytimeCalculation?.startStatus ?? undefined,
		),
		endLaytimeCountingOption: convertToLaytimeOption(
			laytimeCalculation?.endEvent ?? undefined,
			laytimeCalculation?.endStatus ?? undefined,
		),
		startLaytimeCountingTimestamp: laytimeCalculation?.startTimestamp ?? undefined,
		endLaytimeCountingTimestamp: laytimeCalculation?.endTimestamp ?? undefined,
		startLaytimeCountingLabel: laytimeCalculation?.startLabel ?? undefined,
		endLaytimeCountingLabel: laytimeCalculation?.endLabel ?? undefined,
	};

	const { control, handleSubmit, setValue, reset, formState: { errors }, watch } = useForm<CallLaytimeForm>({ defaultValues: defaultValues });
	const values = watch();

	const [ startLaytimeCountingOption, endLaytimeCountingOption, startLaytimeCountingTimestamp, endLaytimeCountingTimestamp ] = useWatch({
		control: control,
		name: [ "startLaytimeCountingOption", "endLaytimeCountingOption", "startLaytimeCountingTimestamp", "endLaytimeCountingTimestamp" ],
	});

	const [ saveLaytimeProperties, { loading } ] = useMutation<UpdateLaytimeCalculationMutation>(UpdateLaytimeCalculationDocument, {
		onCompleted: () => {
			onClose(call.id);
			refetch();
		},
	});

	const handleCancel = () => {
		reset();
		onCancel();
	};

	const save = (data: CallLaytimeForm) => {
		saveLaytimeProperties({
			variables: {
				charterId: charterId,
				input: getLaytimePropertiesInput(call.id, data),
			},
		});
	};

	useEffect(() => {
		reset(defaultValues);
	}, [ call, laytimeCalculation ]);

	const showStartEventLabelInput = values.startLaytimeCountingOption?.type == "event" && values.startLaytimeCountingOption.event == LaytimeCountingEvent.Custom;
	const showEndEventLabelInput = values.endLaytimeCountingOption?.type == "event" && values.endLaytimeCountingOption.event == LaytimeCountingEvent.Custom;

	return (
		<Grid container spacing={2}>
			{/* Start */}
			<Grid size={showStartEventLabelInput ? 4 : 8}>
				<FormGroup overflow="visible" label={intl.formatMessage({ id: "START_LAYTIME" })} required>
					<FormField
						name="startLaytimeCountingOption"
						errors={errors}
					>
						<Controller
							name="startLaytimeCountingOption"
							control={control}
							rules={{ required: true }}
							render={({ field }) => (
								<LaytimeCountingSelect
									{...field}
									additionalEvents={[ LaytimeCountingEvent.StartLaycan ]}
									search={{ callId: call.id, startLaytime: true }}
									onChange={(event) => {
										field.onChange(event);
										if (event?.type == "event") {
											setValue("startLaytimeCountingTimestamp", undefined);
											if (event.event === LaytimeCountingEvent.StartLaycan) {
												setValue("startLaytimeCountingTimestamp", call.pta || undefined);
											}
										} else {
											setValue("startLaytimeCountingTimestamp", event?.statusChange.actualTime ?? undefined);
										}
									}}
								/>
							)}
						/>
					</FormField>
				</FormGroup>
			</Grid>

			{showStartEventLabelInput && (
				<Grid size={4}>
					<FormGroup overflow="visible" label={intl.formatMessage({ id: "LABEL" })} required>
						<FormField
							name="startLaytimeCountingLabel"
							errors={errors}
						>
							<Controller
								name="startLaytimeCountingLabel"
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<Input {...field} />
								)}
							/>
						</FormField>
					</FormGroup>
				</Grid>
			)}

			<Grid size={4}>
				<FormGroup overflow="visible" label={intl.formatMessage({ id: "TIMESTAMP" })} required>
					<FormField
						name="startLaytimeCountingTimestamp"
						errors={errors}
					>
						<Controller
							name="startLaytimeCountingTimestamp"
							control={control}
							rules={{ required: true }}
							render={({ field }) => (
								<DatePicker {...field}
									value={startLaytimeCountingTimestamp}
									showTimeInput
									timeIntervals={1}
									disabled={startLaytimeCountingOption?.type !== "event" || startLaytimeCountingOption.event !== LaytimeCountingEvent.Custom}
								/>
							)}
						/>
					</FormField>
				</FormGroup>
			</Grid>

			{/* End */}
			<Grid size={showEndEventLabelInput ? 4 : 8}>
				<FormGroup overflow="visible" label={intl.formatMessage({ id: "END_LAYTIME" })} required>
					<FormField
						name="endLaytimeCountingOption"
						errors={errors}
					>
						<Controller
							name="endLaytimeCountingOption"
							control={control}
							rules={{ required: true }}
							render={({ field }) => (
								<LaytimeCountingSelect
									{...field}
									search={{ callId: call.id, endLaytime: true }}
									onChange={(event) => {
										field.onChange(event);
										if (event?.type === "event") {
											setValue("endLaytimeCountingTimestamp", undefined);
										} else {
											setValue("endLaytimeCountingTimestamp", event?.statusChange.actualTime ?? undefined);
										}
									}}
								/>
							)}
						/>
					</FormField>
				</FormGroup>
			</Grid>

			{showEndEventLabelInput && (
				<Grid size={4}>
					<FormGroup overflow="visible" label={intl.formatMessage({ id: "LABEL" })} required>
						<FormField
							name="endLaytimeCountingLabel"
							errors={errors}
						>
							<Controller
								name="endLaytimeCountingLabel"
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<Input {...field} />
								)}
							/>
						</FormField>
					</FormGroup>
				</Grid>
			)}

			<Grid size={4}>
				<FormGroup overflow="visible" label={intl.formatMessage({ id: "TIMESTAMP" })} required>
					<FormField
						name="endLaytimeCountingTimestamp"
						errors={errors}
					>
						<Controller
							name="endLaytimeCountingTimestamp"
							control={control}
							rules={{ required: true }}
							render={({ field }) => (
								<DatePicker {...field}
									value={endLaytimeCountingTimestamp}
									showTimeInput
									disabled={endLaytimeCountingOption?.type !== "event" || endLaytimeCountingOption.event !== LaytimeCountingEvent.Custom} />
							)}
						/>
					</FormField>
				</FormGroup>
			</Grid>

			<Grid size={12} sx={{ display: "flex", justifyContent: "end", gap: 1 }}>
				<Button variant="outlined" size="small" onClick={handleCancel}>
					{intl.formatMessage({ id: "CANCEL" })}
				</Button>
				<Button loading={loading} size="small" onClick={handleSubmit(save)}>
					{intl.formatMessage({ id: "SAVE" })}
				</Button>
			</Grid>
		</Grid>
	);
};

const CallLaytimeDetails = ({ laytimeCalculation }: { laytimeCalculation: LaytimeCalculationType }) => {
	const session = useStoreState(store => store.session);

	const {
		startEvent,
		startStatus,
		startTimestamp,
		endEvent,
		endStatus,
		endTimestamp,
		startLabel,
		endLabel,
	} = laytimeCalculation;

	const getStartLabel = () => {
		if (startStatus) {
			const statusLabel = DataFormat.status(startStatus.status, session.locale);
			const cargoNumber = startStatus.cargo.cargoNumber;
			return cargoNumber ? `${statusLabel} - ${intl.formatMessage({ id: "CARGO" })} ${cargoNumber}` : statusLabel;
		} else if (startEvent) {
			return intl.formatMessage({ id: `LAYTIME_COUNTING_EVENT_${startEvent}` });
		}
		return "-";
	};

	const getEndLabel = () => {
		if (endStatus) {
			const statusLabel = DataFormat.status(endStatus.status, session.locale);
			const cargoNumber = endStatus.cargo.cargoNumber;
			return cargoNumber ? `${statusLabel} - ${intl.formatMessage({ id: "CARGO" })} ${cargoNumber}` : statusLabel;
		} else if (endEvent) {
			return intl.formatMessage({ id: `LAYTIME_COUNTING_EVENT_${endEvent}` });
		}
		return "-";
	};

	return (
		<Grid container spacing={2}>
			{/* Start */}
			<Grid size={startLabel ? 4 : 8}>
				<FormGroup overflow="visible" label={intl.formatMessage({ id: "START_LAYTIME" })}>
					{getStartLabel()}
				</FormGroup>
			</Grid>

			{startLabel && (
				<Grid size={4}>
					<FormGroup overflow="visible" label={intl.formatMessage({ id: "LABEL" })}>
						{startLabel}
					</FormGroup>
				</Grid>
			)}

			<Grid size={4}>
				<FormGroup overflow="visible" label={intl.formatMessage({ id: "TIMESTAMP" })}>
					<FormattedDate date={startTimestamp ?? undefined} format="lll" />
				</FormGroup>
			</Grid>

			{/* End */}
			<Grid size={endLabel ? 4 : 8}>
				<FormGroup overflow="visible" label={intl.formatMessage({ id: "END_LAYTIME" })}>
					{getEndLabel()}
				</FormGroup>
			</Grid>

			{endLabel && (
				<Grid size={4}>
					<FormGroup overflow="visible" label={intl.formatMessage({ id: "LABEL" })}>
						{endLabel}
					</FormGroup>
				</Grid>
			)}

			<Grid size={4}>
				<FormGroup overflow="visible" label={intl.formatMessage({ id: "TIMESTAMP" })}>
					<FormattedDate date={endTimestamp ?? undefined} format="lll" />
				</FormGroup>
			</Grid>
		</Grid>
	);
};

type DemurrageCharter = ChildProps<DemurrageCharterQuery, "charter">;

type DemurrageDetailsProps = {
	charter: DemurrageCharter;
	salesCharter: SalesCharter;
	voyage: NonNullable<DemurrageDocumentsVoyageQuery["voyage"]>;
	calculationMethod: NonNullable<Agreement["calculationMethod"]>;
	declaration?: NonNullable<DemurrageCharter["declarations"]>[number];
	onEdit: () => void;
	refetch: () => void;
	laytimeSuggestion?: SuggestLaytime_DemurrageDetailsQuery["suggestLaytime"];
};

const DemurrageDetails = ({ charter, salesCharter, voyage, calculationMethod, declaration, onEdit, refetch, laytimeSuggestion }: DemurrageDetailsProps) => {
	const session = useStoreState(store => store.session);
	const [ showPdfDrawer, setShowPdfDrawer ] = useState(false);

	const calculateTotalLoadedWeight = () => {
		const cargos = salesCharter.cargos ?? [];
		return cargos.reduce((total, cargo) => total + (cargo.totalLoadedWeight ?? 0), 0);
	};

	const totalLoadedWeight = calculateTotalLoadedWeight();

	return <>
		<Card
			disablePadding
			icon={<Payments fontSize="small" color="primary" />}
			title={intl.formatMessage({ id: "DEMURRAGE" })}
			actions={[
				...(declaration?.lastStatusChange ? [ {
					render: () => (
						<div style={{ marginRight: 8 }}>
							<StatusChanger
								statusType={StatusType.Declaration}
								entity={declaration}
								refetch={refetch}
								showHistory
							/>
						</div>
					),
				} ] : []),
				{
					icon: <InsertDriveFile fontSize="small" />,
					tooltip: intl.formatMessage({ id: "PREVIEW_EXPORT_DEMURRAGE_CLAIM" }),
					onClick: () => setShowPdfDrawer(true),
				},
				{
					icon: <Calculate fontSize="small" />,
					tooltip: intl.formatMessage({ id: "DECLARE" }),
					onClick: onEdit,
				},
			]}>
			<Grid
				container
				spacing={2}
				p={1}>
				<Grid container size={12}>
					<Grid size={3}>
						<FormGroup label={Language("SHIPS_CAPACITY_TON")} overflow="hidden">
							{DataFormat.tonnage(voyage.ship?.capacityTon)}
						</FormGroup>
					</Grid>

					<Grid size={3}>
						<FormGroup label={Language("TOTAL_LOADED_WEIGHT")} overflow="hidden">
							{DataFormat.tonnage(totalLoadedWeight)}
						</FormGroup>
					</Grid>

					<Grid size={3}>
						<FormGroup label={Language("CARGO_CHARTER_FIRST_PLANNED_LOADING_DATE")} overflow="hidden">
							<FormattedDate date={salesCharter.firstLoadingDate} format="ll" />
						</FormGroup>
					</Grid>

					<Grid size={3}>
						<FormGroup label={Language("BL_DATE")} overflow="hidden">
							<FormattedDate date={salesCharter.blDate} format="ll" />
						</FormGroup>
					</Grid>
				</Grid>
				<Grid container size={12}>
					{charterHasLaytimeActionWith(salesCharter, CallType.NitrogenPurging) && (
						<Grid size={3}>
							<FormGroup label={intl.formatMessage({ id: `TOTAL_LAYTIME_PER_${CallType.NitrogenPurging}` })} overflow="hidden">
								<RoundedMinutes
									minutes={charter.totalNitrogenPurgingLaytime ?? 0}
									isRounded={calculationMethod.laytimeRoundingScope == DemurrageCalculationRoundingScope.TotalPerActionType}
									isOverridden={!!laytimeSuggestion?.nitrogenPurgingLaytime && charter.totalNitrogenPurgingLaytime != laytimeSuggestion?.nitrogenPurgingLaytime}
									original={laytimeSuggestion?.nitrogenPurgingLaytime}
								/>
							</FormGroup>
						</Grid>
					)}

					{charterHasLaytimeActionWith(salesCharter, CallType.Loading) && (
						<Grid size={3}>
							<FormGroup label={intl.formatMessage({ id: `TOTAL_LAYTIME_PER_${CallType.Loading}` })} overflow="hidden">
								<RoundedMinutes
									minutes={charter.totalLoadingLaytime ?? 0}
									isRounded={calculationMethod.laytimeRoundingScope == DemurrageCalculationRoundingScope.TotalPerActionType}
									isOverridden={!!laytimeSuggestion?.loadingLaytime && charter.totalLoadingLaytime != laytimeSuggestion?.loadingLaytime}
									original={laytimeSuggestion?.loadingLaytime}
								/>
							</FormGroup>
						</Grid>
					)}

					{charterHasLaytimeActionWith(salesCharter, CallType.Discharging) && (
						<Grid size={3}>
							<FormGroup label={intl.formatMessage({ id: `TOTAL_LAYTIME_PER_${CallType.Discharging}` })} overflow="hidden">
								<RoundedMinutes
									minutes={charter.totalDischargingLaytime ?? 0}
									isRounded={calculationMethod.laytimeRoundingScope == DemurrageCalculationRoundingScope.TotalPerActionType}
									isOverridden={!!laytimeSuggestion?.dischargingLaytime && charter.totalDischargingLaytime != laytimeSuggestion?.dischargingLaytime}
									original={laytimeSuggestion?.dischargingLaytime}
								/>
							</FormGroup>
						</Grid>
					)}

					{charterHasLaytimeActionWith(salesCharter, CallType.Cleaning) && (
						<Grid size={3}>
							<FormGroup label={intl.formatMessage({ id: `TOTAL_LAYTIME_PER_${CallType.Cleaning}` })} overflow="hidden">
								<RoundedMinutes
									minutes={charter.totalCleaningLaytime ?? 0}
									isRounded={calculationMethod.laytimeRoundingScope == DemurrageCalculationRoundingScope.TotalPerActionType}
									isOverridden={!!laytimeSuggestion?.cleaningLaytime && charter.totalCleaningLaytime != laytimeSuggestion?.cleaningLaytime}
									original={laytimeSuggestion?.cleaningLaytime}
								/>
							</FormGroup>
						</Grid>
					)}
				</Grid>

				<Grid container size={12}>
					<Grid size={3}>
						<FormGroup
							label={Language("TOTAL_LAYTIME")}
							overflow="hidden">
							<RoundedMinutes
								minutes={charter.totalLaytime ?? 0}
								isRounded={calculationMethod.laytimeRoundingScope == DemurrageCalculationRoundingScope.TotalAllActions}
								isOverridden={!!laytimeSuggestion?.totalLaytime && charter.totalLaytime != laytimeSuggestion?.totalLaytime}
								original={laytimeSuggestion?.totalLaytime}
							/>
						</FormGroup>
					</Grid>

					<Grid size={3}>
						<FormGroup label={Language("LAYTIME_ALLOWED")} overflow="hidden">
							{calculationMethod.laytimeAllowed
								? <FormattedMinutes minutes={calculationMethod.laytimeAllowed} />
								: "-"}
						</FormGroup>
					</Grid>
					{calculationMethod.laytimePeriodRegime && (
						<Grid size={3}>
							<FormGroup label={intl.formatMessage({ id: "PERIOD_REGIME" })} overflow="hidden">
								{intl.formatMessage({ id: `PERIOD_REGIME_${calculationMethod.laytimePeriodRegime}` })}
							</FormGroup>
						</Grid>
					)}
				</Grid>

				<Grid container size={12}>
					<Grid size={3}>
						<FormGroup label={intl.formatMessage({ id: "EXCESS_LAYTIME" })} overflow="hidden">
							<FormattedMinutes minutes={charter.excessLaytime ?? 0} />
						</FormGroup>
					</Grid>

					<Grid size={3}>
						<FormGroup label={intl.formatMessage({ id: `PRICE_PER_${calculationMethod.demurrageCalculationUnit}` })} overflow="hidden">
							{DataFormat.price(calculationMethod.price, charter.contract?.currency?.isoCode as string, session.locale)}
						</FormGroup>
					</Grid>

					<Grid size={3}>
						<FormGroup label={intl.formatMessage({ id: "DEMURRAGE" })} overflow="hidden">
							{DataFormat.price(charter.demurrage, charter.contract?.currency?.isoCode, session.locale)}
						</FormGroup>
					</Grid>
				</Grid>
				{calculationMethod.description && (
					<Grid size={6}>
						<FormGroup label={intl.formatMessage({ id: "DEMURRAGE_CALCULATION_INSTRUCTIONS" })} overflow="hidden">
							<Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "small" }}>
								{calculationMethod.description}
							</Typography>
						</FormGroup>
					</Grid>
				)}
				<Grid size={6}>
					<FormGroup label={intl.formatMessage({ id: "NOTE" })} overflow="hidden">
						<Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "small" }}>
							{charter.laytimeRemarks ?? "-"}
						</Typography>
					</FormGroup>
				</Grid>
			</Grid>
		</Card>

		<DocumentDrawer
			open={showPdfDrawer}
			onClose={() => setShowPdfDrawer(false)}
			url={`charters/demurrage/pdf/export/${charter.id}/${voyage.id}`}
			multilingual
		/>
	</>;
};

type DemurrageEditProps = {
	charter: DemurrageCharter;
	salesCharter: SalesCharter;
	calculationMethod: NonNullable<Agreement["calculationMethod"]>;
	onCancel: () => void;
	onSave: () => void;
	refetch: () => void;
	declaration?: NonNullable<DemurrageCharter["declarations"]>[number];
	laytimeSuggestion?: SuggestLaytime_DemurrageDetailsQuery["suggestLaytime"];
};

const DemurrageEdit = ({ charter, salesCharter, calculationMethod, onCancel, onSave, refetch, laytimeSuggestion, declaration: existingDeclaration }: DemurrageEditProps) => {
	const defaultValues: DemurrageEditFormType = {
		cleaningLaytimeText: laytimeMinutesToHHMM(charter.totalCleaningLaytime),
		cleaningLaytime: charter.totalCleaningLaytime ?? undefined,
		loadingLaytimeText: laytimeMinutesToHHMM(charter.totalLoadingLaytime),
		loadingLaytime: charter.totalLoadingLaytime ?? undefined,
		dischargingLaytimeText: laytimeMinutesToHHMM(charter.totalDischargingLaytime),
		dischargingLaytime: charter.totalDischargingLaytime ?? undefined,
		nitrogenPurgingLaytimeText: laytimeMinutesToHHMM(charter.totalNitrogenPurgingLaytime),
		nitrogenPurgingLaytime: charter.totalNitrogenPurgingLaytime ?? undefined,
		totalLaytimeText: laytimeMinutesToHHMM(charter.totalLaytime),
		totalLaytime: charter.totalLaytime ?? undefined,
		remarks: charter.laytimeRemarks ?? undefined,
	};

	const { control, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<DemurrageEditFormType>({ defaultValues });
	const [ updateLaytime, { loading } ] = useMutation(updateLaytimeAndDeclare_mutation);
	const session = useStoreState(store => store.session);
	const values = watch();

	const handleCancel = () => {
		reset();
		onCancel();
	};

	const save = (data: DemurrageEditFormType) => {
		updateLaytime({
			variables: {
				charterId: Number(charter.id),
				input: {
					cleaningLaytime: data.cleaningLaytime,
					loadingLaytime: data.loadingLaytime,
					dischargingLaytime: data.dischargingLaytime,
					nitrogenPurgingLaytime: data.nitrogenPurgingLaytime,
					totalLaytime: data.totalLaytime,
					remarks: data.remarks,
					declarationId: existingDeclaration?.id,
				},
			},
		}).then(() => {
			onSave();
			refetch();
		});
	};

	const applySuggestion = (
		textField: "cleaningLaytimeText" | "loadingLaytimeText" | "dischargingLaytimeText" | "nitrogenPurgingLaytimeText" | "totalLaytimeText",
		minutesField: "cleaningLaytime" | "loadingLaytime" | "dischargingLaytime" | "nitrogenPurgingLaytime" | "totalLaytime",
	) => {
		if (laytimeSuggestion && minutesField in laytimeSuggestion) {
			const minutes = laytimeSuggestion[minutesField as keyof typeof laytimeSuggestion] as number;
			setValue(minutesField, minutes);
			setValue(textField, laytimeMinutesToHHMM(minutes));
		}
	};

	const renderLaytimeField = (
		textField: "cleaningLaytimeText" | "loadingLaytimeText" | "dischargingLaytimeText" | "nitrogenPurgingLaytimeText" | "totalLaytimeText",
		minutesField: "cleaningLaytime" | "loadingLaytime" | "dischargingLaytime" | "nitrogenPurgingLaytime" | "totalLaytime",
		labelKey: string,
	) => {
		const charterValueMap = {
			cleaningLaytime: charter.totalCleaningLaytime,
			loadingLaytime: charter.totalLoadingLaytime,
			dischargingLaytime: charter.totalDischargingLaytime,
			nitrogenPurgingLaytime: charter.totalNitrogenPurgingLaytime,
		};
		const totalLaytime = (values.nitrogenPurgingLaytime ?? 0) + (values.loadingLaytime ?? 0) + (values.dischargingLaytime ?? 0) + (values.cleaningLaytime ?? 0);

		return <Grid size={3}>
			<FormGroup label={intl.formatMessage({ id: labelKey })} overflow="hidden">
				<FormField name={textField} errors={errors}>
					<Controller
						name={textField}
						control={control}
						rules={{
							validate: (value) => {
								if (!value) {
									return true;
								}
								const minutes = laytimeTextToMinutes(value);
								return minutes != null || intl.formatMessage({ id: "LAYTIME_INVALID_FORMAT" });
							},
						}}
						render={({ field }) => (
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<Input
									ref={field.ref}
									name={field.name}
									value={field.value ?? ""}
									onBlur={field.onBlur}
									placeholder="hh:mm"
									addonAfter={intl.formatMessage({ id: "HOURS" })}
									onChange={(e: ChangeEvent<HTMLInputElement>) => {
										const value = e.target.value;
										field.onChange(value);
										const mins = laytimeTextToMinutes(value);
										setValue(minutesField, mins ?? undefined);

										if (textField !== "totalLaytimeText") {
											const newValues = {
												cleaningLaytime: minutesField === "cleaningLaytime" ? mins : values.cleaningLaytime,
												loadingLaytime: minutesField === "loadingLaytime" ? mins : values.loadingLaytime,
												dischargingLaytime: minutesField === "dischargingLaytime" ? mins : values.dischargingLaytime,
												nitrogenPurgingLaytime: minutesField === "nitrogenPurgingLaytime" ? mins : values.nitrogenPurgingLaytime,
											};
											const totalLaytime = (newValues.cleaningLaytime ?? 0) + (newValues.loadingLaytime ?? 0) + (newValues.dischargingLaytime ?? 0) + (newValues.nitrogenPurgingLaytime ?? 0);
											setValue("totalLaytime", totalLaytime);
											setValue("totalLaytimeText", laytimeMinutesToHHMM(totalLaytime));
										}
									}}
								/>
								{minutesField != "totalLaytime" && laytimeSuggestion?.[minutesField] != values[minutesField] && (() => {
									const isFirstTimeFill = charterValueMap[minutesField] == null;
									return (
										<IconButton
											size="small"
											onClick={() => applySuggestion(textField, minutesField)}
										>
											{isFirstTimeFill ? <AutoFixHigh fontSize="small" /> :
												<Restore fontSize="small" />}
										</IconButton>
									);
								})()}

								{minutesField == "totalLaytime" && values.totalLaytime != totalLaytime && (
									<IconButton
										size="small"
										onClick={() => {
											setValue("totalLaytime", totalLaytime);
											setValue("totalLaytimeText", laytimeMinutesToHHMM(totalLaytime));
										}}
									>
										<Restore fontSize="small" />
									</IconButton>)}
							</Box>
						)}
					/>
				</FormField>
			</FormGroup>
		</Grid>;
	};

	const onRecalculate = () => {
		setValue("cleaningLaytime", laytimeSuggestion?.cleaningLaytime ?? 0);
		setValue("cleaningLaytimeText", laytimeMinutesToHHMM(laytimeSuggestion?.cleaningLaytime ?? 0));
		setValue("loadingLaytime", laytimeSuggestion?.loadingLaytime ?? 0);
		setValue("loadingLaytimeText", laytimeMinutesToHHMM(laytimeSuggestion?.loadingLaytime ?? 0));
		setValue("dischargingLaytime", laytimeSuggestion?.dischargingLaytime ?? 0);
		setValue("dischargingLaytimeText", laytimeMinutesToHHMM(laytimeSuggestion?.dischargingLaytime ?? 0));
		setValue("nitrogenPurgingLaytime", laytimeSuggestion?.nitrogenPurgingLaytime ?? 0);
		setValue("nitrogenPurgingLaytimeText", laytimeMinutesToHHMM(laytimeSuggestion?.nitrogenPurgingLaytime ?? 0));
		setValue("totalLaytime", laytimeSuggestion?.totalLaytime ?? 0);
		setValue("totalLaytimeText", laytimeMinutesToHHMM(laytimeSuggestion?.totalLaytime ?? 0));
	};

	return (
		<Card
			disablePadding
			icon={<Payments fontSize="small" color="primary" />}
			title={intl.formatMessage({ id: "DEMURRAGE" })}
			actions={[
				{
					icon: <Restore fontSize="small" />,
					tooltip: intl.formatMessage({ id: "RECALCULATE" }),
					onClick: onRecalculate,
				},
			]}
		>
			<Grid container spacing={2} p={1}>
				<Grid container size={12}>
					{(charterHasLaytimeActionWith(salesCharter, CallType.NitrogenPurging) || (values.nitrogenPurgingLaytime ?? 0) > 0)
						&& renderLaytimeField("nitrogenPurgingLaytimeText", "nitrogenPurgingLaytime", `TOTAL_LAYTIME_PER_${CallType.NitrogenPurging}`)}
					{(charterHasLaytimeActionWith(salesCharter, CallType.Loading) || (values.loadingLaytime ?? 0) > 0)
						&& renderLaytimeField("loadingLaytimeText", "loadingLaytime", `TOTAL_LAYTIME_PER_${CallType.Loading}`)}
					{(charterHasLaytimeActionWith(salesCharter, CallType.Discharging) || (values.dischargingLaytime ?? 0) > 0)
						&& renderLaytimeField("dischargingLaytimeText", "dischargingLaytime", `TOTAL_LAYTIME_PER_${CallType.Discharging}`)}
					{(charterHasLaytimeActionWith(salesCharter, CallType.Cleaning) || (values.cleaningLaytime ?? 0) > 0)
						&& renderLaytimeField("cleaningLaytimeText", "cleaningLaytime", `TOTAL_LAYTIME_PER_${CallType.Cleaning}`)}
				</Grid>

				<Grid container size={12}>
					{renderLaytimeField("totalLaytimeText", "totalLaytime", "TOTAL_LAYTIME")}
					<Grid size={3}>
						<FormGroup label={Language("LAYTIME_ALLOWED")} overflow="hidden">
							<div style={{ marginTop: 12 }}>
								{calculationMethod.laytimeAllowed
									? <FormattedMinutes minutes={calculationMethod.laytimeAllowed} />
									: "-"}
							</div>
						</FormGroup>
					</Grid>
					<Grid size={3}>
						<FormGroup label={intl.formatMessage({ id: "PERIOD_REGIME" })} overflow="hidden">
							<div style={{ marginTop: 12 }}>
								{calculationMethod.laytimePeriodRegime
									? intl.formatMessage({ id: `PERIOD_REGIME_${calculationMethod.laytimePeriodRegime}` })
									: "-"}
							</div>
						</FormGroup>
					</Grid>
				</Grid>
				<Grid container size={12}>
					<Grid size={3}>
						<FormGroup label={intl.formatMessage({ id: "EXCESS_LAYTIME" })} overflow="hidden">
							<i>
								<FormattedMinutes minutes={values.totalLaytime && calculationMethod.laytimeAllowed
									? Math.max(0, values.totalLaytime - calculationMethod.laytimeAllowed)
									: 0} />
							</i>
						</FormGroup>
					</Grid>

					<Grid size={3}>
						<FormGroup label={intl.formatMessage({ id: `PRICE_PER_${calculationMethod.demurrageCalculationUnit}` })} overflow="hidden">
							{DataFormat.price(calculationMethod.price, charter.contract?.currency?.isoCode as string, session.locale)}
						</FormGroup>
					</Grid>

					<Grid size={3}>
						<FormGroup label={intl.formatMessage({ id: "DEMURRAGE" })} overflow="hidden">
							<Tooltip title={intl.formatMessage({ id: "ESTIMATED_DEMURRAGE" })}>
								<i>
									±
									{DataFormat.price(values.totalLaytime && calculationMethod.laytimeAllowed
										? Math.max(0, values.totalLaytime - calculationMethod.laytimeAllowed) * (calculationMethod.price ?? 0) / 60
										: 0,
									charter.contract?.currency?.isoCode, session.locale)}
								</i>
							</Tooltip>
						</FormGroup>
					</Grid>
				</Grid>
				{calculationMethod.description && (
					<Grid size={6}>
						<FormGroup label={intl.formatMessage({ id: "DEMURRAGE_CALCULATION_INSTRUCTIONS" })} overflow="hidden">
							<Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "small" }}>
								{calculationMethod.description}
							</Typography>
						</FormGroup>
					</Grid>
				)}
				<Grid size={6}>
					<FormGroup label={intl.formatMessage({ id: "NOTE" })} overflow="hidden">
						<FormField name="remarks" errors={errors}>
							<Controller
								name="remarks"
								control={control}
								render={({ field }) => (
									<Textarea {...field} value={field.value ?? ""} />
								)}
							/>
						</FormField>
					</FormGroup>
				</Grid>

				<Grid size={12} sx={{ display: "flex", justifyContent: "end", gap: 1 }}>
					<Button variant="outlined" size="small" onClick={handleCancel}>
						{intl.formatMessage({ id: "CANCEL" })}
					</Button>
					<Button loading={loading} size="small" onClick={handleSubmit(save)}>
						{intl.formatMessage({ id: "DECLARE" })}
					</Button>
				</Grid>
			</Grid>
		</Card>
	);
};

type DemurrageBlockProps = {
	charter: DemurrageCharter;
	salesCharter: SalesCharter;
	voyage: NonNullable<DemurrageDocumentsVoyageQuery["voyage"]>;
	agreement?: Agreement;
	refetch: () => void;
	laytimeSuggestion?: SuggestLaytime_DemurrageDetailsQuery["suggestLaytime"];
}
const DemurrageBlock = ({ charter, salesCharter, voyage, agreement, refetch, laytimeSuggestion }: DemurrageBlockProps) => {
	const [ isEditing, setIsEditing ] = useState(false);

	const calculationMethod = agreement?.calculationMethod ?? undefined;

	if (!calculationMethod || !agreement) {
		return <div style={{ marginBottom: 16 }}>
			<Card
				disablePadding
				icon={<Payments fontSize="small" color="primary" />}
				title={intl.formatMessage({ id: "DEMURRAGE" })}>
				<Grid container spacing={2} p={1}>
					<Grid size={12}>
						<Box mt={1} sx={{ display: "flex", justifyContent: "center" }}>
							<Typography component="span" fontStyle="italic" fontSize="small">{intl.formatMessage({ id: "NO_DEMURRAGE_CALCULATION_AGREEMENT" })}</Typography>
						</Box>
					</Grid>
				</Grid>
			</Card>
		</div>;
	}

	return <div style={{ marginBottom: 16 }}>
		{isEditing ? (
			<DemurrageEdit
				charter={charter}
				salesCharter={salesCharter}
				calculationMethod={calculationMethod}
				declaration={charter?.declarations?.find(
					d => (d.declarationValues?.some(v => v?.agreement?.id === agreement.id)) ||
					d.invoiceGroupType?.id === agreement.invoiceGroupType?.id,
				)}
				onCancel={() => setIsEditing(false)}
				onSave={() => setIsEditing(false)}
				refetch={refetch}
				laytimeSuggestion={laytimeSuggestion}
			/>
		) : (
			<DemurrageDetails
				charter={charter}
				salesCharter={salesCharter}
				voyage={voyage}
				calculationMethod={calculationMethod}
				declaration={charter.declarations?.find(
					d => (d.declarationValues?.some(v => v?.agreement?.id === agreement.id)) ||
					d.invoiceGroupType?.id === agreement.invoiceGroupType?.id,
				)}
				onEdit={() => setIsEditing(true)}
				refetch={refetch}
				laytimeSuggestion={laytimeSuggestion}
			/>
		)}
	</div>;
};

const DemurrageDocuments = ({ voyage, refetch }: { voyage: NonNullable<DemurrageDocumentsVoyageQuery["voyage"]>; refetch: () => void }) => {
	return <DetailsBlock height={Height.Auto}>
		<VoyageDocuments refetch={refetch} voyage={voyage} />
	</DetailsBlock>;
};

const RoundedMinutes = ({ minutes, isRounded, isOverridden, original }: { minutes: number, isRounded: boolean, isOverridden: boolean, original: number | undefined }) => {
	const e = <FormattedMinutes minutes={minutes} />;

	if (isOverridden) {
		return <Tooltip title={
			original
				? <>{intl.formatMessage({ id: "LAYTIME_VALUE_OVERRIDDEN" })}: <FormattedMinutes minutes={original} /></>
				: intl.formatMessage({ id: "LAYTIME_VALUE_OVERRIDDEN" })
		}>
			<span style={{ display: "flex", alignItems: "center", gap: 4 }}>
				{e}
				<ManageHistory fontSize="inherit" color="action" />
			</span>
		</Tooltip>;
	} else if (isRounded) {
		return <Tooltip title={intl.formatMessage({ id: "LAYTIME_VALUE_ROUNDED" })}>
			<span style={{ display: "flex", alignItems: "center", gap: 4 }}>
				{e}
				<BrowseGallery fontSize="inherit" color="action" />
			</span>
		</Tooltip>;
	} else {
		return e;
	}
};

export default DemurrageTab;
